<script setup>
// Astra DS · Button — **el botón del sistema. Uno solo.**
//
// 🔴 **Master con variantes, y nada de recetas sueltas.** Encargo de Pablo,
// 18.09.26: «debe haber un master y variantes; si no, cada cambio es una
// pesadilla». Y lo era: el 18.09 había **cinco** maneras de pintar un botón
// —este, `CtaButton`, `ChipAction` y dos copias a mano en el CSS de `DoorCard` y
// de la portada—, así que cambiar el hover obligaba a tocar cinco sitios y a
// acordarse de los cinco.
//
// Desde aquí manda esto:
//
//   forma   contorno · macizo · acento · inverso · limpio
//   tamaño  micro · sm · md · lg · area
//   estado  hover, foco, deshabilitado — iguales en todas las formas
//
// **`ChipAction` y `CtaButton` siguen existiendo como envoltorios** que traducen
// su API a estas propiedades: así las veinte páginas que ya los usan no cambian
// una línea, y el estilo vive en un único fichero. Cuando una página necesite un
// botón nuevo, usa este.
//
// ⚠️ Clase `ds-btn` (NO `btn`) para no colisionar con Bootstrap, que todavía
// está en la hoja global.
import { computed } from "vue";
import AstraIcon from "@/components/icons/AstraIcon.vue";

const props = defineProps({
  label: { type: String, default: "" },

  /**
   * Qué clase de acción es.
   *
   * - `ghost` / `secondary` · contorno. La acción que acompaña.
   * - `primary` · macizo en rojo de acción. La que manda.
   * - `accent` · macizo en rojo, sin filete: el chip de estado («En emisión»).
   * - `inverse` · macizo en tinta. Los botones de tienda.
   * - `release` · macizo en azul, con filete del mismo azul: el chip NUEVO
   *   LANZAMIENTO (19.10.26). Es un rótulo, no una acción, y no cambia con el modo.
   * - `quiet` · contorno fino y letra pequeña: la llamada de una puerta o de
   *   una tarjeta, que acompaña a una imagen y no compite con el titular.
   */
  variant: {
    type: String,
    default: "ghost",
    validator: (v) =>
      ["primary", "secondary", "ghost", "accent", "inverse", "quiet", "release"].includes(v),
  },

  // `area` es la caja de 42 del área de cuenta; `micro` la de las puertas.
  size: {
    type: String,
    default: "md",
    validator: (v) => ["micro", "sm", "md", "lg", "area"].includes(v),
  },

  /**
   * Sobre qué se apoya el botón.
   *
   * `imagen` es la misma regla que el player: **lo pide la superficie, no la
   * pantalla**. Sobre una foto, la tinta del lienzo no vale —ni la clara ni la
   * oscura—, así que el botón pasa a la tinta que no se invierte y su filete a
   * una línea muy suave.
   */
  surface: {
    type: String,
    default: "canvas",
    validator: (v) => ["canvas", "imagen"].includes(v),
  },

  // Icono de la casa a la derecha de la etiqueta. `CtaButton` lo usa siempre.
  icono: { type: String, default: "" },
  iconoTam: { type: Number, default: 12 },
  // El icono delante de la etiqueta y no detrás: los chips del selector de
  // vistas de Radio (20.10.26) lo llevan a la izquierda.
  iconoDelante: { type: Boolean, default: false },

  /**
   * **Botón de icono**: sin etiqueta visible, caja cuadrada.
   *
   * Se enciende solo con poner `icono` y dejar `label` vacío, y entonces
   * `etiqueta` es obligatoria: un control sin texto tiene que decir qué hace al
   * lector de pantalla. El lado va en píxeles porque cada sitio tiene el suyo
   * —40 en la fila de enlaces, 46 en la cabecera, 64 en la cabecera del
   * diálogo— y esa medida la pone el hueco, no el botón.
   */
  etiqueta: { type: String, default: "" },
  lado: { type: Number, default: 40 },

  // El punto del chip de estado, delante de la etiqueta.
  dot: { type: Boolean, default: false },

  // El nodo del área fija 210 para los CTA de la página de cuenta.
  wide: { type: Boolean, default: false },

  disabled: { type: Boolean, default: false },
  type: { type: String, default: "button" },

  // Un chip que solo informa —«En emisión»— es un `span`: no se pulsa, no entra
  // en el tabulador y no se anuncia como control.
  as: {
    type: String,
    default: "button",
    validator: (v) => ["button", "span"].includes(v),
  },
  href: { type: String, default: "" },
  to: { type: [String, Object], default: null },
});

// Sin etiqueta visible y con icono: es un botón de icono.
const soloIcono = computed(() => !!props.icono && !props.label);

const tag = computed(() => {
  if (props.to) return "router-link";
  if (props.href) return "a";
  return props.as;
});

// Solo se enlaza la prop que corresponde al tag. Pasar `href` a un
// <router-link> lo cuela como atributo de fallthrough y SOBRESCRIBE el href que
// el propio RouterLink genera: con `undefined` lo borra y el <a> queda sin href
// (enlace muerto, sin cursor ni estados :hover/:focus).
const linkProps = computed(() => {
  if (props.to) return { to: props.to };
  if (props.href) return { href: props.href };
  return {};
});
</script>

<template>
  <component
    :is="tag"
    class="ds-btn"
    :class="[
      `ds-btn--${variant}`,
      `ds-btn--${size}`,
      {
        'ds-btn--imagen': surface === 'imagen',
        'ds-btn--solo-icono': soloIcono,
        'is-wide': wide,
      },
    ]"
    :style="soloIcono ? { '--ds-btn-lado': `${lado}px` } : null"
    :aria-label="etiqueta || undefined"
    v-bind="linkProps"
    :type="tag === 'button' ? type : undefined"
    :disabled="tag === 'button' ? disabled : undefined"
    :aria-disabled="disabled || undefined"
  >
    <span v-if="dot" class="ds-btn__dot" aria-hidden="true" />
    <!-- La etiqueta por prop cubre el 95 % de los casos; el hueco es para
         cuando dentro va algo más, como el indicador de carga de la baja. -->
    <AstraIcon v-if="icono && iconoDelante" :name="icono" :size="iconoTam" />
    <span v-if="!soloIcono" class="ds-btn__label"><slot>{{ label }}</slot></span>
    <AstraIcon v-if="icono && !iconoDelante" :name="icono" :size="iconoTam" />
  </component>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

.ds-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  box-sizing: border-box;
  font-family: var(--font-mono-ds);
  font-weight: 400;
  line-height: 1;
  letter-spacing: 2px;
  text-transform: uppercase;
  text-decoration: none;
  border: 1px solid transparent;
  border-radius: 0; // brutalista: esquinas rectas (anula Bootstrap)
  cursor: pointer;
  // Solo lo que cambia: el relleno ya no entra en el hover de los contornos.
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &:disabled,
  &[aria-disabled="true"] {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
}

// ── Tamaños ──
.ds-btn--micro {
  @include t.mono-micro;
  padding: 7px 13px;
  letter-spacing: 1.8px;
}

.ds-btn--sm {
  padding: 8px 16px;
  font-size: 12px;
}

.ds-btn--md {
  padding: 10px 20px;
  font-size: 12px;
}

.ds-btn--lg {
  padding: 14px 24px;
  font-size: 14px;
}

// La caja del área de cuenta: 42 de alto y 19 de aire a los lados.
.ds-btn--area {
  @include t.mono-micro;
  height: 42px;
  padding: 0 19px;
  letter-spacing: 1.8px;

  &.is-wide {
    width: 210px;
    justify-content: space-between;
  }
}

// ── Formas ──
//
// 🔴 **Un solo contorno y un solo hover, en toda la web.** Encargo de Pablo
// (18.09.26): antes había tres filetes distintos —`border/strong` en unos
// botones, `border/default` en las llamadas de las tarjetas y una línea clara
// sobre foto— y dos hovers, uno blanco y otro negro según dónde cayera el
// botón. Unificar el código no bastaba: había que unificar lo que se ve.
//
//   reposo  filete `border/default` · letra `text/primary`
//   hover   filete y letra en **rojo de acción**
//
// ⚠️ **El filete de reposo pasó a `border/default` el 21.09.26**, con la regla
// de un solo borde general: `default` es el contorno de todo control y `strong`
// se queda para lo que contornea por los cuatro lados. **El chip sí es de los
// segundos**, así que `ChipAction` y los chips de la barra de filtros se lo
// devuelven encima de este.
//
// **El hover va en rojo y no en tinta** justamente porque es lo único que se lee
// igual en los dos temas y también encima de una foto: con tinta, el mismo gesto
// salía blanco en la portada y negro en las tarjetas.
.ds-btn--ghost,
.ds-btn--secondary,
.ds-btn--quiet {
  background: transparent;
  border-color: var(--color-border-default);
  color: var(--color-text-primary);

  &:hover:not(:disabled),
  &:active:not(:disabled) {
    border-color: var(--color-text-accent);
    color: var(--color-text-accent);
  }
}

// Las macizas sí cambian de fondo: no tienen filete que marcar, y sin relleno
// se quedarían sin respuesta.
.ds-btn--primary {
  background: var(--color-action-primary-default);
  border-color: var(--color-action-primary-default);
  color: var(--color-text-on-action);

  &:hover:not(:disabled) {
    background: var(--color-action-primary-hover);
    border-color: var(--color-action-primary-hover);
  }

  &:active:not(:disabled) {
    background: var(--color-action-primary-pressed, var(--color-action-primary-hover));
  }
}

.ds-btn--accent {
  background: var(--color-bg-accent);
  color: var(--color-text-on-action);
}

.ds-btn--inverse {
  background: var(--color-text-primary);
  color: var(--color-bg-canvas);
}

// Relleno y filete en `color/release/bg`, texto en `color/release/text`
// (`Chip Variant=lanzamiento 2505:139`). Constante en los dos modos.
.ds-btn--release {
  background: var(--color-release-bg);
  border-color: var(--color-release-bg);
  color: var(--color-release-text);
}

// ── Botón de icono ──
// Caja cuadrada del lado que pida el hueco, sin relleno propio: lo que manda es
// la medida. El icono va centrado y el foco se marca hacia dentro, porque casi
// siempre es una celda pegada a otras y un halo por fuera se saldría del filete.
.ds-btn--solo-icono {
  width: var(--ds-btn-lado, 40px);
  height: var(--ds-btn-lado, 40px);
  padding: 0;
  gap: 0;

  &:focus-visible {
    outline-offset: -2px;
  }
}

// ── Sobre imagen ──
// Lo único que cambia es el reposo: la tinta del lienzo no vale encima de una
// foto, así que la letra va en la tinta que no se invierte y el filete en la
// línea suave del player. **El hover es el mismo rojo que en todas partes.**
.ds-btn--imagen {
  color: var(--color-text-on-ink);
  border-color: color-mix(in srgb, var(--color-text-on-ink) 35%, transparent);
}

.ds-btn__dot {
  width: 6px;
  height: 6px;
  flex: none;
  background: currentColor;
}

.ds-btn__label {
  white-space: nowrap;
}
</style>
